import React, { useState, useEffect } from 'react';
import {
  Save,
  RefreshCw,
  Phone,
  Mail,
  User,
  Calendar,
  FileText,
  Star,
  MapPin,
  ChevronDown,
  ChevronRight,
  List,
  ArrowRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import api from '@/utils/api/optimized-api';

interface FacebookFieldOption {
  key: string;
  value: string;
}

interface FacebookFormField {
  id: number;
  uuid: string;
  facebook_field_key: string;
  facebook_field_label?: string;
  facebook_field_type: string;
  is_required: boolean;
  field_order: number;
  mapped_crm_column?: string;
  field_options?: FacebookFieldOption[];
  is_active: boolean;
}

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: FacebookFormField[];
}

interface CrmColumnOption {
  opt_id: number;
  opt_name: string;
  opt_value: string;
  uuid: string;
}

interface CrmColumn {
  col_id: number;
  column_name: string;
  display_name: string;
  data_type: string;
  is_required: boolean;
  category: string;
  options?: CrmColumnOption[];
}

interface FacebookFormFieldMappingProps {
  form: FacebookLeadForm;
  isOpen: boolean;
  onClose: () => void;
  onSave: (formId: string, fieldMapping: any) => Promise<void>;
}

const FacebookFormFieldMapping: React.FC<FacebookFormFieldMappingProps> = ({
  form,
  isOpen,
  onClose,
  onSave
}) => {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [valueMapping, setValueMapping] = useState<Record<string, Record<string, string>>>({});
  const [crmColumns, setCrmColumns] = useState<CrmColumn[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen && form) {
      loadCrmColumns();
      initializeMapping();
    }
  }, [isOpen, form]);

  const loadCrmColumns = async () => {
    setIsLoadingColumns(true);
    try {
      const response = await api.req({
        url: '/integrations/crm/lead-columns?include_options=true',
        method: api.Method.GET
      });

      const data = response;

      if (data.success && data.data && Array.isArray(data.data)) {
        const mappedColumns: CrmColumn[] = data.data.map((col: any) => ({
          col_id: col.col_id,
          column_name: col.col_name,
          display_name: col.col_label,
          data_type: col.col_type,
          is_required: col.col_required,
          category: getCategoryFromColumnName(col.col_name),
          options: col.options || []
        }));

        setCrmColumns(mappedColumns);
      } else {
        throw new Error(data.message || 'Failed to load CRM columns');
      }
    } catch (error) {
      console.error('Error loading CRM columns:', error);
      toast.error('Failed to load CRM columns');
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const getCategoryFromColumnName = (columnName: string): string => {
    const contactFields = ['name', 'first_name', 'last_name', 'email', 'phone', 'alternate_phone', 'whatsapp_number'];
    const businessFields = ['company_name', 'job_title', 'industry', 'website'];

    if (contactFields.includes(columnName)) return 'contact';
    if (businessFields.includes(columnName)) return 'business';
    return 'custom';
  };

  const initializeMapping = () => {
    const fieldMappingData = form.field_mapping || {};
    const currentMapping: Record<string, string> = {};
    const currentValueMapping: Record<string, Record<string, string>> = {};

    Object.keys(fieldMappingData).forEach(key => {
      if (key === '_value_mappings') {
        Object.assign(currentValueMapping, fieldMappingData[key]);
      } else {
        currentMapping[key] = fieldMappingData[key];
      }
    });

    if (form.formFields) {
      form.formFields.forEach(field => {
        if (!currentMapping[field.facebook_field_key]) {
          const suggestion = getSuggestedMapping(field);
          if (suggestion) {
            currentMapping[field.facebook_field_key] = suggestion;
          }
        }
      });
    }

    setMapping(currentMapping);
    setValueMapping(currentValueMapping);
  };

  const getFieldTypeIcon = (fieldType: string) => {
    switch (fieldType) {
      case 'PHONE_NUMBER':
      case 'PHONE':
        return <Phone className="h-4 w-4 text-green-500" />;
      case 'EMAIL':
        return <Mail className="h-4 w-4 text-brandColor" />;
      case 'FULL_NAME':
      case 'FIRST_NAME':
      case 'LAST_NAME':
        return <User className="h-4 w-4 text-purple-500" />;
      case 'DATE_TIME':
        return <Calendar className="h-4 w-4 text-orange-500" />;
      case 'MULTIPLE_CHOICE':
        return <List className="h-4 w-4 text-blue-500" />;
      case 'TRACKING_PARAMETER':
        return <MapPin className="h-4 w-4 text-amber-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getFieldPriority = (fieldType: string) => {
    const priorityMap: Record<string, number> = {
      'PHONE_NUMBER': 100,
      'PHONE': 100,
      'EMAIL': 90,
      'FULL_NAME': 80,
      'FIRST_NAME': 75,
      'LAST_NAME': 75,
      'TRACKING_PARAMETER': 60, // Show tracking params after contact info but before other fields
      'SHORT_ANSWER': 50,
      'MULTIPLE_CHOICE': 40,
      'CONDITIONAL': 30,
      'DATE_TIME': 20,
      'CUSTOM': 10
    };
    return priorityMap[fieldType] || 10;
  };

  const getSuggestedMapping = (field: FacebookFormField): string => {
    const fieldKey = field.facebook_field_key.toLowerCase();
    const fieldType = field.facebook_field_type;

    if (fieldType === 'EMAIL' || fieldKey.includes('email')) return 'email';
    if (fieldType === 'PHONE_NUMBER' || fieldType === 'PHONE' || fieldKey.includes('phone')) return 'phone';
    if (fieldType === 'FULL_NAME' || fieldKey.includes('name')) return 'name';
    if (fieldType === 'FIRST_NAME' || fieldKey.includes('first_name')) return 'first_name';
    if (fieldType === 'LAST_NAME' || fieldKey.includes('last_name')) return 'last_name';
    if (fieldKey.includes('company')) return 'company_name';
    if (fieldKey.includes('job') || fieldKey.includes('title')) return 'job_title';
    if (fieldKey.includes('city')) return 'city';
    if (fieldKey.includes('state')) return 'state';
    if (fieldKey.includes('country')) return 'country';
    if (fieldKey.includes('grade')) return 'grade';
    if (fieldKey.includes('branch')) return 'branch';

    return '';
  };

  const handleMappingChange = (facebookFieldKey: string, crmColumn: string) => {
    setMapping(prev => ({
      ...prev,
      [facebookFieldKey]: crmColumn
    }));

    if (valueMapping[facebookFieldKey]) {
      setValueMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[facebookFieldKey];
        return newMapping;
      });
    }
  };

  const handleValueMappingChange = (facebookFieldKey: string, facebookValue: string, crmOptValue: string) => {
    setValueMapping(prev => ({
      ...prev,
      [facebookFieldKey]: {
        ...(prev[facebookFieldKey] || {}),
        [facebookValue]: crmOptValue
      }
    }));
  };

  const toggleFieldExpanded = (fieldKey: string) => {
    setExpandedFields(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  const getColumnByName = (columnName: string): CrmColumn | undefined => {
    return crmColumns.find(col => col.column_name === columnName);
  };

  const isSelectColumn = (columnName: string): boolean => {
    const column = getColumnByName(columnName);
    return column?.data_type === 'select' || column?.data_type === 'radio';
  };

  const getColumnOptions = (columnName: string): CrmColumnOption[] => {
    const column = getColumnByName(columnName);
    return column?.options || [];
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const combinedMapping = {
        ...mapping,
        _value_mappings: valueMapping
      };
      await onSave(form.form_id, combinedMapping);
    } catch (error) {
      console.error('Error saving mapping:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getCrmColumnsByCategory = (category: string) => {
    return crmColumns.filter(col => col.category === category);
  };

  const sortedFormFields = form.formFields?.sort((a, b) => {
    const priorityDiff = getFieldPriority(b.facebook_field_type) - getFieldPriority(a.facebook_field_type);
    if (priorityDiff !== 0) return priorityDiff;
    return a.field_order - b.field_order;
  }) || [];

  if (!isOpen) return null;

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Field Mapping"
      subtitle={`Map Facebook form fields to CRM columns for ${form.form_name}`}
      icon={<MapPin className="w-6 h-6" />}
    >
      <>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {isLoadingColumns ? (
              <div className="flex items-center justify-center h-32">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin text-brandColor" />
                  <span className="text-gray-600">Loading CRM columns...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  {sortedFormFields.map((field) => {
                    const currentMapping = mapping[field.facebook_field_key] || '';
                    const isSelect = currentMapping && isSelectColumn(currentMapping);
                    const columnOptions = isSelect ? getColumnOptions(currentMapping) : [];
                    const isExpanded = expandedFields[field.facebook_field_key];
                    const hasFacebookOptions = field.field_options && field.field_options.length > 0;
                    const showValueMapping = isSelect && columnOptions.length > 0 && hasFacebookOptions;

                    return (
                      <div
                        key={field.id}
                        className="border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                      >
                        {/* Main field mapping row */}
                        <div className="p-3">
                          <div className="flex items-center justify-between gap-4">
                            {/* Field Name */}
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              {getFieldTypeIcon(field.facebook_field_type)}
                              <span className="font-medium text-gray-900 dark:text-white truncate">
                                {field.facebook_field_label || field.facebook_field_key}
                              </span>
                              {field.is_required && <Star className="h-3 w-3 text-red-500 flex-shrink-0" />}
                              {field.facebook_field_type === 'TRACKING_PARAMETER' && (
                                <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-1.5 py-0.5 rounded">
                                  Static: {field.field_options?.[0]?.value || 'N/A'}
                                </span>
                              )}
                              {hasFacebookOptions && field.facebook_field_type !== 'TRACKING_PARAMETER' && (
                                <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                  {field.field_options?.length} options
                                </span>
                              )}
                            </div>

                            {/* Mapping Select */}
                            <div className="flex items-center gap-2 flex-1 max-w-xs">
                              <select
                                value={currentMapping}
                                onChange={(e) => handleMappingChange(field.facebook_field_key, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                              >
                                <option value="">-- Select Column --</option>
                                {getCrmColumnsByCategory('contact').length > 0 && (
                                  <optgroup label="Contact Fields">
                                    {getCrmColumnsByCategory('contact').map((column) => (
                                      <option key={column.column_name} value={column.column_name}>
                                        {column.display_name} {column.data_type === 'select' ? '(Select)' : ''}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {getCrmColumnsByCategory('business').length > 0 && (
                                  <optgroup label="Business Fields">
                                    {getCrmColumnsByCategory('business').map((column) => (
                                      <option key={column.column_name} value={column.column_name}>
                                        {column.display_name} {column.data_type === 'select' ? '(Select)' : ''}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                {getCrmColumnsByCategory('custom').length > 0 && (
                                  <optgroup label="Custom Fields">
                                    {getCrmColumnsByCategory('custom').map((column) => (
                                      <option key={column.column_name} value={column.column_name}>
                                        {column.display_name} {column.data_type === 'select' ? '(Select)' : ''}
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                              </select>

                              {/* Expand button for value mapping */}
                              {showValueMapping && (
                                <button
                                  onClick={() => toggleFieldExpanded(field.facebook_field_key)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    isExpanded
                                      ? 'text-brandColor bg-cyan-50 dark:bg-cyan-900/30'
                                      : 'text-gray-500 hover:text-brandColor hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                  title="Map option values"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Value mapping section (expandable) - Direct dropdown mapping */}
                        {showValueMapping && isExpanded && (
                          <div className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 p-4">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                              <span>Map Facebook options to CRM options:</span>
                            </div>
                            <div className="space-y-2">
                              {field.field_options?.map((fbOption, idx) => {
                                const currentValueMap = valueMapping[field.facebook_field_key] || {};
                                const selectedCrmValue = currentValueMap[fbOption.value] || '';

                                return (
                                  <div key={idx} className="flex items-center gap-3">
                                    {/* Facebook option (readonly) */}
                                    <div className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300">
                                      {fbOption.value}
                                    </div>

                                    <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />

                                    {/* CRM option dropdown */}
                                    <select
                                      value={selectedCrmValue}
                                      onChange={(e) => handleValueMappingChange(
                                        field.facebook_field_key,
                                        fbOption.value,
                                        e.target.value
                                      )}
                                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                    >
                                      <option value="">-- Select CRM Option --</option>
                                      {columnOptions.map((crmOpt) => (
                                        <option key={crmOpt.opt_id} value={crmOpt.opt_value}>
                                          {crmOpt.opt_name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div></div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isSaving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{isSaving ? 'Saving...' : 'Save Mapping'}</span>
              </button>
            </div>
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default FacebookFormFieldMapping;
